import React, { useRef } from 'react';
import { useFormState } from './useFormState';
import {
    useGetIndividualOwnerAssetsQuery,
    useUpdateExperienceMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { useNavigate } from 'react-router';
import { FormProvider, Path, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import NavigationActions from './NavigationActions';
import {
    getPartialObjectSchema,
    mergeStepValues,
    pickStepValues,
    prepareExperiencePayload,
} from './utils';
import toast from '../../../shared/Toast';
import { getStepSchema } from './schema';
import { Container, Fade } from '@mui/material';
import NavigationHandler from './NavigationHandler';

const StepForm = ({ children }: { children: React.ReactNode }) => {
    const { experience, step, steps, stepIndex, handleNext, isSaveDisabled } =
        useFormState();
    const navigate = useNavigate();
    const loggedInUser = UserRole();

    const { data: allAssets } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
    });

    const { schema, partialSchema, defaultValues } = getStepSchema(
        step,
        experience,
        allAssets ?? [],
    );

    const [updateExperience, { isLoading }] = useUpdateExperienceMutation();

    const methods = useForm<z.infer<typeof schema>>({
        defaultValues: mergeStepValues(
            defaultValues,
            pickStepValues(partialSchema ?? schema, experience),
        ),
        resolver: zodResolver(schema),
    });

    const { getValues, clearErrors, setError } = methods;

    // Saving and exiting leaves the page with a dirty form, so the unsaved
    // changes confirmation has to be skipped for that navigation.
    const bypassNavigationBlocker = useRef(false);

    const onSubmit = async (data: z.infer<typeof schema>) => {
        if (isSaveDisabled) return;

        try {
            const { discounts, ...rest } = data;

            await updateExperience({
                owner: loggedInUser?.company,
                id: experience.id,
                experience: {
                    ...rest,
                    type: experience.type,
                    // add device name to the payload, else send null/undefined
                    device: data.device
                        ? {
                              uniqueId: data.device.uniqueId,
                              enabled: data.device.enabled,
                              name: experience.name,
                          }
                        : data.device,
                    currentStep: steps[stepIndex + 1],
                },
            }).unwrap();

            handleNext();
        } catch (error) {
            toast.error('Failed to save experience');
            console.error(error);
        }
    };

    const handleSave = async () => {
        if (isSaveDisabled) return;

        try {
            clearErrors();

            // validate partial data
            const validationSchema = getPartialObjectSchema(
                partialSchema ?? schema,
            );
            const result = validationSchema.safeParse(getValues());

            if (!result.success) {
                result.error.issues.forEach((issue) => {
                    setError(
                        issue.path.join('.') as Path<z.infer<typeof schema>>,
                        {
                            message: issue.message,
                        },
                    );
                });
                return;
            }

            const { discounts, ...rest } = result.data;

            await updateExperience({
                owner: loggedInUser?.company,
                id: experience.id,
                experience: prepareExperiencePayload(
                    {
                        ...rest,
                        device: result.data.device
                            ? {
                                  uniqueId: result.data.device.uniqueId,
                                  enabled: result.data.device.enabled,
                                  name: experience.name,
                              }
                            : result.data.device,
                    },
                    experience.type,
                ),
            }).unwrap();

            bypassNavigationBlocker.current = true;
            navigate(`/viewExperiences/all/${loggedInUser?.company}`);
        } catch (error) {
            toast.error('Failed to save experience');
            console.error(error);
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                style={{ height: '100%' }}
            >
                <Fade in>
                    <Container maxWidth='md' sx={{ height: '100%' }}>
                        {children}
                    </Container>
                </Fade>
                <NavigationActions
                    isLoading={isLoading}
                    handleSave={handleSave}
                />
                <NavigationHandler bypassRef={bypassNavigationBlocker} />
            </form>
        </FormProvider>
    );
};

export default StepForm;
